import { useSceneStore } from '../stores/sceneStore';
import { useTimelineStore } from '../stores/timelineStore';
import { usePuppetStore, initializeStarterPuppet } from '../stores/puppetStore';
import type { MotorConfig } from '../stores/puppetStore';
import { sceneSchema, formatValidationErrors } from '../schemas/sceneSchema';
import type {
  SceneExport,
  ValidationError,
  PuppetExport,
  GroupExport,
  PlacedGroupExport,
} from '../schemas/sceneSchema';

/**
 * Scene Export/Import Utilities (Parametric Animation Refactor)
 *
 * Updated for new Tool interface:
 * - Uses targetRotation and easing instead of speed/intensity
 *
 * Added puppet support:
 * - Exports puppets array with all puppet data and nested tools
 * - Imports restore puppets to puppetStore
 * - Backward compatible with scenes without puppets field
 *
 * Added groups support (Task Group 7):
 * - Exports groups array with all group data and tool snapshots
 * - Imports restore groups to timelineStore
 * - Backward compatible with scenes without groups field
 */

/**
 * Result type for import operations
 */
export interface ImportResult {
  success: boolean;
  errors?: ValidationError[];
}

/**
 * Export current scene to JSON format
 * Serializes all tabs, tools, tracks, puppets, groups, and metadata
 *
 * @returns SceneExport object ready for JSON serialization
 */
export function exportScene(): SceneExport {
  const sceneState = useSceneStore.getState();
  const timelineState = useTimelineStore.getState();
  const puppetState = usePuppetStore.getState();

  // Get current active tab's timeline data
  const activeTab = sceneState.tabs.find(
    (tab) => tab.id === sceneState.activeTabId
  );

  if (!activeTab) {
    throw new Error('No active tab found for export');
  }

  // Map timeline tools to export format with tool names
  // Updated for parametric animation (targetRotation, easing)
  const toolsExport = timelineState.tools.map((tool) => {
    const toolDefinition = puppetState.getToolById(tool.toolId);

    return {
      id: tool.id,
      toolId: tool.toolId,
      name: toolDefinition?.name || 'Unknown Tool',
      bodyPart: tool.bodyPart,
      startTime: tool.startTime,
      duration: tool.duration,
      trackNumber: tool.trackNumber,
      targetRotation: tool.targetRotation,
      easing: tool.easing,
      targetPosition: tool.targetPosition,
      puppetId: tool.puppetId,
    };
  });

  // Build tabs array with timeline data
  const tabsExport = sceneState.tabs.map((tab) => {
    // For active tab, use current timeline state
    const isActiveTab = tab.id === sceneState.activeTabId;

    const tabExport: {
      id: string;
      name: string;
      order: number;
      cameraPosition: { x: number; y: number; z: number };
      modelPath?: string;
      timeline: {
        duration: number;
        tools: typeof toolsExport;
      };
    } = {
      id: tab.id,
      name: tab.name,
      order: tab.order,
      cameraPosition: tab.cameraPosition,
      timeline: {
        duration: isActiveTab ? timelineState.duration : 10,
        tools: isActiveTab ? toolsExport : [],
      },
    };

    // Only include modelPath if it's defined
    if (tab.modelPath) {
      tabExport.modelPath = tab.modelPath;
    }

    return tabExport;
  });

  // Export all puppets with their nested tools and motor configuration
  // Include activePuppet config for motor positions (startingPosition, dockingPosition)
  const activePuppetConfig = puppetState.activePuppet;

  const puppetsExport: PuppetExport[] = puppetState.puppets.map((puppet) => {
    // If this is the active puppet, include motor config and positions
    const isActivePuppet =
      activePuppetConfig?.id === puppet.id ||
      (puppetState.puppets.length === 1 && activePuppetConfig);

    const puppetExport: PuppetExport = {
      id: puppet.id,
      name: puppet.name,
      modelPath: puppet.modelPath,
      description: puppet.description,
      url: puppet.url,
      tools: puppet.tools.map((tool) => ({
        id: tool.id,
        name: tool.name,
        bodyPart: tool.bodyPart,
        type: tool.type,
        defaultDuration: tool.defaultDuration,
        defaultEasing: tool.defaultEasing,
        minDegrees: tool.minDegrees,
        maxDegrees: tool.maxDegrees,
        color: tool.color,
        isPrebuilt: tool.isPrebuilt,
        icon: tool.icon,
        httpEndpoints: tool.httpEndpoints,
        dockingPosition: tool.dockingPosition,
        startingPosition: tool.startingPosition,
        axis: tool.axis,
      })),
    };

    // Add motor configuration and positions from activePuppet if available
    if (isActivePuppet && activePuppetConfig) {
      puppetExport.motors = activePuppetConfig.motors;
      puppetExport.startingPosition = activePuppetConfig.startingPosition;
      puppetExport.dockingPosition = activePuppetConfig.dockingPosition;
      puppetExport.movementSpeed = activePuppetConfig.movementSpeed;
    }

    return puppetExport;
  });

  // Export all groups with their tool snapshots (Task Group 7)
  const groupsExport: GroupExport[] = timelineState.groups.map((group) => ({
    id: group.id,
    name: group.name,
    trackCount: group.trackCount,
    tools: group.tools.map((tool) => ({
      toolId: tool.toolId,
      bodyPart: tool.bodyPart,
      duration: tool.duration,
      targetRotation: tool.targetRotation,
      easing: tool.easing,
      relativeStartTime: tool.relativeStartTime,
      relativeTrackNumber: tool.relativeTrackNumber,
      targetPosition: tool.targetPosition,
      puppetId: tool.puppetId,
    })),
  }));

  // Export placed group instances on the timeline
  const placedGroupsExport: PlacedGroupExport[] =
    timelineState.placedGroups.map((pg) => ({
      id: pg.id,
      groupId: pg.groupId,
      trackNumber: pg.trackNumber,
      startTime: pg.startTime,
      puppetId: pg.puppetId,
    }));

  // Build complete scene export
  const sceneExport: SceneExport = {
    version: sceneState.metadata.version,
    createdAt: sceneState.metadata.createdAt,
    metadata: {
      name: 'Puppet Animation Scene',
      description: 'Exported scene from Puppet Studio',
    },
    tabs: tabsExport,
    puppets: puppetsExport,
    groups: groupsExport.length > 0 ? groupsExport : undefined,
    placedGroups:
      placedGroupsExport.length > 0 ? placedGroupsExport : undefined,
  };

  // Only include puppetModel if it's defined
  if (activeTab.modelPath) {
    sceneExport.puppetModel = activeTab.modelPath;
  }

  return sceneExport;
}

/**
 * Generate filename for scene export
 * Format: puppet-scene-YYYY-MM-DD-HHmmss.json
 *
 * @returns Timestamped filename
 */
export function generateExportFilename(): string {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `puppet-scene-${year}-${month}-${day}-${hours}${minutes}${seconds}.json`;
}

/**
 * Trigger browser download of scene JSON
 *
 * @param sceneData - Scene export data
 * @param filename - Optional custom filename (defaults to timestamped name)
 */
export function downloadSceneJSON(
  sceneData: SceneExport,
  filename?: string
): void {
  const jsonString = JSON.stringify(sceneData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename || generateExportFilename();
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Import scene from JSON data
 * Validates against schema and loads into stores
 *
 * Handles backward compatibility:
 * - If puppets field is present, restore puppets to puppetStore
 * - If puppets field is missing (old scene format), keep existing puppets or create Starter Puppet
 * - If groups field is present, restore groups to timelineStore
 * - If groups field is missing (old scene format), set groups to empty array
 *
 * @param jsonData - Parsed JSON data (can be any type)
 * @returns ImportResult with success status and any validation errors
 */
export function importScene(jsonData: unknown): ImportResult {
  // Validate against schema
  const validationResult = sceneSchema.safeParse(jsonData);

  if (!validationResult.success) {
    const errors = formatValidationErrors(validationResult.error);
    return {
      success: false,
      errors,
    };
  }

  const sceneData = validationResult.data;

  try {
    // Load data into sceneStore
    useSceneStore.setState({
      tabs: sceneData.tabs.map((tab) => ({
        id: tab.id,
        name: tab.name,
        order: tab.order,
        cameraPosition: tab.cameraPosition,
        modelPath: tab.modelPath,
        createdAt: sceneData.createdAt,
      })),
      activeTabId: sceneData.tabs[0]?.id || null,
      metadata: {
        version: sceneData.version,
        createdAt: sceneData.createdAt,
        lastModifiedAt: new Date().toISOString(),
      },
    });

    // Load first tab's timeline data into timelineStore
    // Updated for parametric animation (targetRotation, easing)
    const firstTab = sceneData.tabs[0];

    // Restore groups from scene data (Task Group 7)
    // If groups field is missing, default to empty array
    const groupsToRestore = sceneData.groups
      ? sceneData.groups.map((group) => ({
          id: group.id,
          name: group.name,
          trackCount: group.trackCount ?? 1,
          tools: group.tools.map((tool) => ({
            toolId: tool.toolId,
            bodyPart: tool.bodyPart,
            duration: tool.duration,
            targetRotation: tool.targetRotation,
            easing: tool.easing,
            relativeStartTime: tool.relativeStartTime,
            relativeTrackNumber: tool.relativeTrackNumber,
            targetPosition: tool.targetPosition,
            puppetId: tool.puppetId,
          })),
        }))
      : [];

    const placedGroupsToRestore = sceneData.placedGroups
      ? sceneData.placedGroups.map((pg) => ({
          id: pg.id,
          groupId: pg.groupId,
          trackNumber: pg.trackNumber,
          startTime: pg.startTime,
          puppetId: pg.puppetId,
        }))
      : [];

    if (firstTab) {
      useTimelineStore.setState({
        tracks: Array.from({ length: 3 }, (_, i) => ({
          number: i + 1,
          label: `Track ${i + 1}`,
        })),
        tools: firstTab.timeline.tools.map((tool) => ({
          id: tool.id,
          toolId: tool.toolId,
          trackNumber: tool.trackNumber,
          startTime: tool.startTime,
          duration: tool.duration,
          bodyPart: tool.bodyPart,
          targetRotation: tool.targetRotation,
          easing: tool.easing,
          targetPosition: tool.targetPosition,
          puppetId: tool.puppetId,
        })),
        groups: groupsToRestore,
        placedGroups: placedGroupsToRestore,
        duration: firstTab.timeline.duration,
        playheadPosition: 0,
        isPlaying: false,
        playbackSpeed: 1.0,
      });
    }

    // Handle puppets import
    if (sceneData.puppets && sceneData.puppets.length > 0) {
      // Restore puppets from scene file
      const puppets = sceneData.puppets.map((puppet) => ({
        id: puppet.id,
        name: puppet.name,
        modelPath: puppet.modelPath || '',
        description: puppet.description,
        url: puppet.url,
        tools: puppet.tools.map((tool) => ({
          id: tool.id,
          name: tool.name,
          bodyPart: tool.bodyPart,
          type: tool.type || ('prebuilt' as const),
          defaultDuration: tool.defaultDuration,
          defaultEasing: tool.defaultEasing,
          minDegrees: tool.minDegrees,
          maxDegrees: tool.maxDegrees,
          color: tool.color,
          isPrebuilt: tool.isPrebuilt,
          icon: tool.icon,
          httpEndpoints: tool.httpEndpoints,
          dockingPosition: tool.dockingPosition ?? 0,
          startingPosition: tool.startingPosition ?? 0,
          axis: tool.axis ?? ('x' as const),
        })),
      }));

      // Check if first puppet has motor configuration (new format)
      const firstPuppet = sceneData.puppets[0];
      let activePuppetConfig = null;

      if (
        firstPuppet.motors ||
        firstPuppet.startingPosition ||
        firstPuppet.dockingPosition
      ) {
        // Restore motor configuration from imported puppet
        const defaultPuppet = usePuppetStore.getState().getDefaultPuppet();
        activePuppetConfig = {
          ...defaultPuppet,
          id: firstPuppet.id,
          name: firstPuppet.name,
          modelPath: firstPuppet.modelPath || '',
          motors: firstPuppet.motors
            ? (firstPuppet.motors.map((m) => ({
                ...m,
                id: m.id || `motor-${m.bodyPart}`,
              })) as MotorConfig[])
            : defaultPuppet.motors,
          startingPosition:
            firstPuppet.startingPosition || defaultPuppet.startingPosition,
          dockingPosition:
            firstPuppet.dockingPosition || defaultPuppet.dockingPosition,
          movementSpeed:
            firstPuppet.movementSpeed || defaultPuppet.movementSpeed,
        };
      }

      // Update puppetStore with imported puppets and motor config
      usePuppetStore.setState({
        puppets,
        activePuppetId: puppets[0].id,
        ...(activePuppetConfig && { activePuppet: activePuppetConfig }),
      });
    } else {
      // Backward compatibility: no puppets field in scene
      // Keep existing puppets or ensure Starter Puppet exists
      const currentPuppets = usePuppetStore.getState().puppets;
      if (currentPuppets.length === 0) {
        // Initialize Starter Puppet if no puppets exist
        initializeStarterPuppet();
      }
      // Otherwise, keep existing puppets (don't overwrite)
    }

    return {
      success: true,
    };
  } catch (error) {
    // Handle any runtime errors during import
    return {
      success: false,
      errors: [
        {
          path: 'root',
          message:
            error instanceof Error
              ? error.message
              : 'Unknown error during import',
          code: 'runtime_error',
          suggestion: 'Check the scene data format and try again',
        },
      ],
    };
  }
}

/**
 * Read JSON file from File object
 *
 * @param file - File object from file input
 * @returns Promise resolving to parsed JSON data
 */
export async function readJSONFile(file: File): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const jsonString = event.target?.result as string;
        const jsonData = JSON.parse(jsonString);
        resolve(jsonData);
      } catch (error) {
        reject(
          new Error(
            `Failed to parse JSON: ${error instanceof Error ? error.message : 'Unknown error'}`
          )
        );
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
}
